// hk-hospitals.js — canonical workplace taxonomy: the sector list, plus the
// canonical list of Hong Kong hospitals grouped by sector.
//
// Loaded as a plain script before screens.jsx so window.HK_HOSPITALS and the
// lookup helpers are available to the onboarding flow and profile screens.
//
// Shape:
//   HK_HOSPITALS = {
//     public:  [ { key, en, zh, cluster }, … ],   // HA hospitals, by cluster
//     private: [ { key, en, zh }, … ],
//   }
//
// `key` is a stable slug stored in profiles.hospital_key. The en/zh labels are
// what we render; hospital_en / hospital_zh on the profile row are denormalised
// copies of the chosen label so older code paths (and admin search) keep working
// without a join.
//
// Public list follows the Hospital Authority's seven clusters. Private list
// covers the registered private hospitals in HK. Keep additions alphabetical
// within each cluster/group.
//
// ⚠️ NOT every HK nurse works in a hospital. Sectors come in two KINDS:
//   • 'list' (public / private) — resolves to a canonical hospital_key.
//   • 'text' (NGO, clinic, elderly home, school, other) — there is no
//     authoritative registry for these, so the user types a free-text
//     organisation name which lands in hospital_en / hospital_zh with a NULL
//     hospital_key. Route every such value through sanitizeWorkplaceName().
// Adding a hospital-shaped gate that only accepts a hospital_key locks out the
// large NGO / community / clinic population — that was a real 2026-08 user
// report. Keep new sector ids OUT of the 'public'/'private' namespace: course
// eligibility (RESTRICTION in data.js) matches restriction.sector against
// profiles.hospital_sector, so a non-hospital sector correctly never matches a
// hospital-internal course.
(function () {
  // Workplace sectors, in display order. `kind` drives the picker: 'list'
  // shows the hospital dropdown, 'text' shows a free-text organisation name.
  // `id` is what lands in profiles.hospital_sector — the DB CHECK constraint
  // (supabase-migration-workplace-sectors.sql) mirrors this list, so adding an
  // id here REQUIRES the matching migration or the write is rejected.
  const SECTORS = [
    { id: 'public',  kind: 'list', en: 'Public hospital',  zh: '公立醫院' },
    { id: 'private', kind: 'list', en: 'Private hospital', zh: '私家醫院' },
    { id: 'ngo',     kind: 'text', en: 'NGO / social service', zh: '社福機構（NGO）',
      phEn: 'e.g. Tung Wah Group of Hospitals', phZh: '例如：東華三院' },
    { id: 'clinic',  kind: 'text', en: 'Clinic / medical centre', zh: '診所 / 醫務中心',
      phEn: 'e.g. Kowloon Bay Health Centre', phZh: '例如：九龍灣健康中心' },
    { id: 'elderly', kind: 'text', en: 'Elderly / residential home', zh: '護老院 / 院舍',
      phEn: 'e.g. Yan Chai Care Home', phZh: '例如：仁濟護理安老院' },
    { id: 'school',  kind: 'text', en: 'School / institution', zh: '學校 / 院校',
      phEn: 'e.g. HKU School of Nursing', phZh: '例如：香港大學護理學院' },
    { id: 'other',   kind: 'text', en: 'Other', zh: '其他',
      phEn: 'Your workplace', phZh: '你的工作單位' },
  ];
  const SECTOR_BY_ID = {};
  for (const s of SECTORS) SECTOR_BY_ID[s.id] = s;

  // Longest free-text workplace we store. Counted in CODE POINTS (not UTF-16
  // units) so a Chinese name gets its full 60 characters and no surrogate pair
  // is ever sliced in half. Mirrored by the DB length CHECK (which is bounded
  // more loosely, at 80, so canonical hospital labels also fit).
  const WORKPLACE_NAME_MAX = 60;

  function sectorById(id) { return SECTOR_BY_ID[id] || null; }
  // 'list' | 'text' | null (null = unknown/legacy/empty sector).
  function sectorKind(id) { const s = SECTOR_BY_ID[id]; return s ? s.kind : null; }

  // Both regexes are built from strings of escapes (rather than regex
  // literals) so this SOURCE FILE stays pure ASCII — a literal control
  // character here would be invisible in review and could be mangled by any
  // tool that touches the file.
  //
  // Pass 1: controls that SEPARATE words become a space, so a name pasted
  // across a tab or newline collapses to 'Tung Wah Group', not 'TungWahGroup'.
  //   \u0009-\u000D  tab, LF, VT, FF, CR
  //   \u001C-\u001F  file/group/record/unit separators
  //   \u0085, \u2028, \u2029  NEL, line separator, paragraph separator
  const WORKPLACE_SPACE_RE = new RegExp(
    '[\\u0009-\\u000D\\u001C-\\u001F\\u0085\\u2028\\u2029]', 'g');
  // Pass 2: characters deleted outright. They carry no width, so removing one
  // must NOT leave a space behind — a spoofing run sits INSIDE a word.
  //   \u0000-\u001F, \u007F-\u009F  the remaining C0 / C1 controls
  //   \u200B-\u200F, \u202A-\u202E, \u2066-\u2069  zero-width + bidi controls
  //   \uFEFF  BOM / zero-width no-break space
  const WORKPLACE_STRIP_RE = new RegExp(
    '[\\u0000-\\u001F\\u007F-\\u009F\\u200B-\\u200F\\u202A-\\u202E'
    + '\\u2066-\\u2069\\uFEFF]', 'g');

  // Normalise a user-typed organisation name before it is stored or displayed.
  //
  // This is the ONLY sanitiser for free-text workplace input — call it on every
  // write path (onboarding finish, Edit Profile save). React escapes markup on
  // render, so the risk here is not script injection but DISPLAY SPOOFING: a
  // bidi override or zero-width run makes a stored name render as something
  // other than what it is (admin lists, CSV exports, notification bodies). Those
  // code points are stripped outright rather than escaped, along with C0/C1
  // controls and line/paragraph separators. Whitespace is collapsed and the
  // result is capped; the DB CHECK constraint is the backstop for anything that
  // reaches PostgREST without passing through here.
  function sanitizeWorkplaceName(input) {
    let s = String(input == null ? '' : input);
    s = s.replace(WORKPLACE_SPACE_RE, ' ');
    s = s.replace(WORKPLACE_STRIP_RE, '');
    s = s.replace(/\s+/g, ' ').trim();
    const cps = Array.from(s);
    return cps.length > WORKPLACE_NAME_MAX ? cps.slice(0, WORKPLACE_NAME_MAX).join('') : s;
  }

  const PUBLIC = [
    // Hong Kong East Cluster (HKEC)
    { key: 'pyneh',  en: 'Pamela Youde Nethersole Eastern Hospital', zh: '東區尤德夫人那打素醫院', cluster: 'HKEC' },
    { key: 'rh',     en: 'Ruttonjee Hospital',                       zh: '律敦治醫院',             cluster: 'HKEC' },
    { key: 'twgh_wc',en: 'Tung Wah Eastern Hospital',               zh: '東華東院',               cluster: 'HKEC' },
    { key: 'wch',    en: 'Wong Chuk Hang Hospital',                  zh: '黃竹坑醫院',             cluster: 'HKEC' },
    { key: 'cch',    en: 'Cheshire Home, Chung Hom Kok',             zh: '舂磡角慈氏護養院',       cluster: 'HKEC' },
    { key: 'sjh',    en: 'St. John Hospital',                        zh: '長洲醫院',               cluster: 'HKEC' },

    // Hong Kong West Cluster (HKWC)
    { key: 'qmh',    en: 'Queen Mary Hospital',                      zh: '瑪麗醫院',               cluster: 'HKWC' },
    { key: 'gh',     en: 'Grantham Hospital',                        zh: '葛量洪醫院',             cluster: 'HKWC' },
    { key: 'twh',    en: 'Tung Wah Hospital',                        zh: '東華醫院',               cluster: 'HKWC' },
    { key: 'twgh_fy',en: 'Fung Yiu King Hospital',                   zh: '馮堯敬醫院',             cluster: 'HKWC' },
    { key: 'mmrc',   en: 'MacLehose Medical Rehabilitation Centre',  zh: '麥理浩復康院',           cluster: 'HKWC' },
    { key: 'dkch',   en: 'Duchess of Kent Children’s Hospital', zh: '根德公爵夫人兒童醫院',   cluster: 'HKWC' },
    { key: 'tsmh',   en: 'Tsan Yuk Hospital',                        zh: '贊育醫院',               cluster: 'HKWC' },

    // Kowloon Central Cluster (KCC)
    { key: 'qeh',    en: 'Queen Elizabeth Hospital',                 zh: '伊利沙伯醫院',           cluster: 'KCC' },
    { key: 'kh',     en: 'Kowloon Hospital',                         zh: '九龍醫院',               cluster: 'KCC' },
    { key: 'hkbh',   en: 'Hong Kong Buddhist Hospital',              zh: '香港佛教醫院',           cluster: 'KCC' },
    { key: 'hkeh',   en: 'Hong Kong Eye Hospital',                   zh: '香港眼科醫院',           cluster: 'KCC' },
    { key: 'rttc',   en: 'Rehabaid Centre',                          zh: '復康用具資源中心',       cluster: 'KCC' },

    // Kowloon East Cluster (KEC)
    { key: 'uch',    en: 'United Christian Hospital',                zh: '基督教聯合醫院',         cluster: 'KEC' },
    { key: 'tkoh',   en: 'Tseung Kwan O Hospital',                   zh: '將軍澳醫院',             cluster: 'KEC' },
    { key: 'hhh',    en: 'Haven of Hope Hospital',                   zh: '靈實醫院',               cluster: 'KEC' },

    // Kowloon West Cluster (KWC)
    { key: 'pmh',    en: 'Princess Margaret Hospital',               zh: '瑪嘉烈醫院',             cluster: 'KWC' },
    { key: 'cmc',    en: 'Caritas Medical Centre',                   zh: '明愛醫院',               cluster: 'KWC' },
    { key: 'ksh',    en: 'Kwong Wah Hospital',                       zh: '廣華醫院',               cluster: 'KWC' },
    { key: 'oltm',   en: 'Our Lady of Maryknoll Hospital',          zh: '聖母醫院',               cluster: 'KWC' },
    { key: 'ych',    en: 'Yan Chai Hospital',                        zh: '仁濟醫院',               cluster: 'KWC' },
    { key: 'nlth',   en: 'North Lantau Hospital',                    zh: '北大嶼山醫院',           cluster: 'KWC' },
    { key: 'wtsh',   en: 'Wong Tai Sin Hospital',                    zh: '黃大仙醫院',             cluster: 'KWC' },
    { key: 'tch',    en: 'TWGHs Wong Tai Sin Hospital',             zh: '東華三院黃大仙醫院',     cluster: 'KWC' },

    // New Territories East Cluster (NTEC)
    { key: 'pwh',    en: 'Prince of Wales Hospital',                 zh: '威爾斯親王醫院',         cluster: 'NTEC' },
    { key: 'aht',    en: 'Alice Ho Miu Ling Nethersole Hospital',    zh: '雅麗氏何妙齡那打素醫院', cluster: 'NTEC' },
    { key: 'ndh',    en: 'North District Hospital',                  zh: '北區醫院',               cluster: 'NTEC' },
    { key: 'sh',     en: 'Shatin Hospital',                          zh: '沙田醫院',               cluster: 'NTEC' },
    { key: 'tph',    en: 'Tai Po Hospital',                          zh: '大埔醫院',               cluster: 'NTEC' },
    { key: 'cnnh',   en: 'Cheshire Home, Shatin',                    zh: '沙田慈氏護養院',         cluster: 'NTEC' },
    { key: 'bbh',    en: 'Bradbury Hospice',                         zh: '白普理寧養中心',         cluster: 'NTEC' },

    // New Territories West Cluster (NTWC)
    { key: 'tmh',    en: 'Tuen Mun Hospital',                        zh: '屯門醫院',               cluster: 'NTWC' },
    { key: 'poh',    en: 'Pok Oi Hospital',                          zh: '博愛醫院',               cluster: 'NTWC' },
    { key: 'tswh',   en: 'Tin Shui Wai Hospital',                    zh: '天水圍醫院',             cluster: 'NTWC' },
    { key: 'cph',    en: 'Castle Peak Hospital',                     zh: '青山醫院',               cluster: 'NTWC' },
    { key: 'slh',    en: 'Siu Lam Hospital',                         zh: '小欖醫院',               cluster: 'NTWC' },
    { key: 'twah',   en: 'Tuen Mun Eye Centre',                      zh: '屯門眼科中心',           cluster: 'NTWC' },
  ];

  const PRIVATE = [
    { key: 'hkah_sw', en: 'Hong Kong Adventist Hospital – Stubbs Road', zh: '香港港安醫院（司徒拔道）' },
    { key: 'hkah_tw', en: 'Hong Kong Adventist Hospital – Tsuen Wan',   zh: '荃灣港安醫院' },
    { key: 'hksh',    en: 'Hong Kong Sanatorium & Hospital',                 zh: '養和醫院' },
    { key: 'mph',     en: 'Matilda International Hospital',                   zh: '明德國際醫院' },
    { key: 'cmh',     en: 'Canossa Hospital (Caritas)',                      zh: '嘉諾撒醫院' },
    { key: 'sph',     en: 'St. Paul’s Hospital',                        zh: '聖保祿醫院' },
    { key: 'sth',     en: 'St. Teresa’s Hospital',                      zh: '聖德肋撒醫院' },
    { key: 'bh',      en: 'Baptist Hospital',                                zh: '浸信會醫院' },
    { key: 'eh',      en: 'Evangel Hospital',                                zh: '播道醫院' },
    { key: 'gjh',     en: 'Gleneagles Hospital Hong Kong',                   zh: '港怡醫院' },
    { key: 'uh',      en: 'Union Hospital',                                  zh: '仁安醫院' },
    { key: 'phc',     en: 'Precious Blood Hospital (Caritas)',               zh: '寶血醫院（明愛）' },
    { key: 'cuhkmc',  en: 'CUHK Medical Centre',                             zh: '香港中文大學醫院' },
    { key: 'hkbh_pr', en: 'Hong Kong Baptist Hospital',                      zh: '香港浸信會醫院' },
  ];

  // Flat lookup by key → { sector, en, zh, cluster? }
  const BY_KEY = {};
  for (const h of PUBLIC)  BY_KEY[h.key] = { ...h, sector: 'public' };
  for (const h of PRIVATE) BY_KEY[h.key] = { ...h, sector: 'private' };

  // Best-effort match of a free-text hospital name (legacy hospital_en /
  // hospital_zh) against the canonical list. Returns the matching entry
  // (with sector) or null. Used to migrate existing profiles that were
  // onboarded before the dropdown existed.
  function matchHospital(text) {
    if (!text) return null;
    const norm = (s) => String(s || '')
      .toLowerCase()
      .replace(/[‘’']/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const q = norm(text);
    if (!q) return null;
    // Exact (en or zh), then substring either direction.
    for (const h of Object.values(BY_KEY)) {
      if (norm(h.en) === q || norm(h.zh) === q) return h;
    }
    for (const h of Object.values(BY_KEY)) {
      const en = norm(h.en);
      const zh = norm(h.zh);
      if (en && (en.includes(q) || q.includes(en))) return h;
      if (zh && (zh === q || text.includes(h.zh))) return h;
    }
    return null;
  }

  function hospitalLabel(key, lang) {
    const h = BY_KEY[key];
    if (!h) return '';
    return lang === 'zh' ? (h.zh || h.en) : h.en;
  }

  // Resolve a profile row's workplace to { en, zh, sector } for display.
  //
  // The single source of truth for "what workplace does this row mean?", shared
  // by the Profile header and Edit Profile so they can't disagree.
  //
  // ⚠️ matchHospital's fuzzy back-match is applied ONLY to list-kind (or
  //    unknown/legacy) sectors. It matches on substrings in either direction, so
  //    running it over a free-text sector would resolve an NGO called "Caritas"
  //    to 明愛醫院 — silently rewriting the user's own answer. A text sector's
  //    stored name is always taken verbatim, falling back to the sector label
  //    when the (optional) organisation name is blank.
  function resolveWorkplace(p) {
    const empty = { en: '', zh: '', sector: null };
    if (!p) return empty;
    const sector = p.hospital_sector || null;
    const kind = sectorKind(sector);
    if (kind === 'text') {
      const s = SECTOR_BY_ID[sector];
      const nameZh = sanitizeWorkplaceName(p.hospital_zh || p.hospital_en || '');
      const nameEn = sanitizeWorkplaceName(p.hospital_en || p.hospital_zh || '');
      return { en: nameEn || s.en, zh: nameZh || s.zh, sector };
    }
    const matched = BY_KEY[p.hospital_key] ||
      matchHospital(p.hospital_en || p.hospital_zh);
    if (matched) return { en: matched.en, zh: matched.zh || matched.en, sector: matched.sector };
    return {
      en: p.hospital_en || p.hospital_zh || '',
      zh: p.hospital_zh || p.hospital_en || '',
      sector,
    };
  }

  window.HK_HOSPITALS = { public: PUBLIC, private: PRIVATE };
  window.HK_HOSPITALS_BY_KEY = BY_KEY;
  window.WORKPLACE_SECTORS = SECTORS;
  window.WORKPLACE_NAME_MAX = WORKPLACE_NAME_MAX;
  window.sectorById = sectorById;
  window.sectorKind = sectorKind;
  window.sanitizeWorkplaceName = sanitizeWorkplaceName;
  window.resolveWorkplace = resolveWorkplace;
  window.matchHospital = matchHospital;
  window.hospitalLabel = hospitalLabel;
})();
